import * as colors from "./colors";


const typographyScale = {
  pageHeroTitle: { base: "1.75rem", sm: "2.5rem", md: "4.125rem", laptopL: "4.5rem" },
  pageHeroSubtitle: { base: "0.875rem", sm: "0.925rem", md: "1.5rem" },
  sectionTitle: { base: "1.25rem", sm: "2.75rem", md: "3.5rem", laptopL: "4.25rem" },
  sectionSubtitle: { base: "0.8rem", sm: "1.5rem" },
  cardTitle: { base: "0.9rem", sm: "2rem" },
  cardSubtitle: { base: "0.8rem", sm: "1.25rem" },
  cardContent: { base: "0.8rem", sm: "1.5rem" },
};

const responsiveFontSize = (sizes) => ({ theme }) =>
  Object.entries(sizes)
    .map(([breakpoint, size]) =>
      breakpoint === "base"
        ? `font-size: ${size};`
        : `${theme.breakpoints.up(breakpoint)} { font-size: ${size}; }`
    )
    .join("\n");

/*
 * Keeps a centered section subtitle to two lines above mobile: a wide measure,
 * and a font that shrinks with the viewport (capped at the sectionSubtitle
 * size) so tablets get the same two-line fit as desktop. Place it after
 * sectionSubtitleSize in the component so it overrides the font size.
 */
const sectionSubtitleTwoLines = ({ theme }) => `
  max-width: 72rem;
  ${theme.breakpoints.up("sm")} { font-size: min(${typographyScale.sectionSubtitle.sm}, 2vw); }
`;

const pageHeroTitleSize = responsiveFontSize(typographyScale.pageHeroTitle);
const pageHeroSubtitleSize = responsiveFontSize(typographyScale.pageHeroSubtitle);
const sectionTitleSize = responsiveFontSize(typographyScale.sectionTitle);
const sectionSubtitleSize = responsiveFontSize(typographyScale.sectionSubtitle);
const cardTitleSize = responsiveFontSize(typographyScale.cardTitle);
const cardSubtitleSize = responsiveFontSize(typographyScale.cardSubtitle);
const cardContentSize = responsiveFontSize(typographyScale.cardContent);

/*
 * Global theme fonts. Read them from the theme (`theme.fonts.body`,
 * `theme.fonts.display`) or import `fonts` where there is no theme.
 * body matches the web app's redesignFontBody.
 */
const fonts = {
  body: '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  display: '"PlayfairDisplay", "Playfair Display", serif',
};

const commonThemeColors = {
  black: colors.BLACK,
  white: colors.WHITE,
  grey: colors.CREAM_BG,
  dark: colors.DARK,
  darkGrey: colors.RIVER_BED,
  transparent13: colors.WHITE_ALPHA13,
  whiteAlpha77: colors.WHITE_ALPHA77,
  error: colors.RED_ERROR,
  //TODO: remove textMuted from here and its dependencies as added at text.muted
  textMuted: colors.BATTLESHIP_GREY,
  highlight: colors.PINK,
  borderWhite: colors.BORDER_WHITE,
  popUpBlurDesktop: colors.POP_UP_BLUR_DESKTOP,
  popUpBlurMobile: colors.POP_UP_BLUR_MOBILE,
  orange: colors.ORANGE,
  outerSpace: colors.OUTERSPACE,
  blueCardBackground: `linear-gradient(204deg, ${colors.OCEAN_BLUE} -0.76%, ${colors.SKY_BLUE} 44.59%, ${colors.B_BLUE} 141%)`,
  bronzeCardBackground: `linear-gradient(204deg, ${colors.BROWN_BEAR} -0.76%, ${colors.CLAY_BROWN} 44.59%, ${colors.MILK_CHOCOLATE} 141%)`,
  silverCardBackground: `linear-gradient(203.94deg, ${colors.CLOUDY_GREY} -0.76%, ${colors.SILVER_CHALICE} 44.59%, ${colors.VAMPIRE_GREY} 141%)`,
  platinumCardBackground: 'linear-gradient(135deg, #F9F9F9 0%, #D4D4D4 40%, #FAFAFA 60%, #BDBDBD 100%)',
  platinumCardText: colors.Dark,
  goldCardBackground: `linear-gradient(203.94deg, ${colors.HAZEL} -0.76%, ${colors.TUMERIC} 44.59%, ${colors.YELLOWY_BROWN} 141%)`,
  membershipPlatinum: colors.redesignMembershipPlatinum,
  membershipGold: colors.redesignMembershipGold,
  membershipBronze: colors.redesignMembershipBronze,
  membershipClassPass: colors.redesignMembershipClassPass,
  primaryCta: colors.redesignPrimaryCta,
};

const themeColors = {
  light: {
    colors: { ...commonThemeColors },
    background: {
      default: colors.White,
      paper: colors.White,
    },
    text: {
      primary: colors.Dark,
      muted: colors.Grey800, // subtitles and secondary text; dark enough to read clearly
      secondary: colors.PrimaryMain,
    },
    primary: {
      light: colors.PrimaryLight,
      main: colors.PrimaryMain,
      dark: colors.PrimaryDark,
      contrastText: colors.White,
    },
    secondary: {
      light: colors.SecondaryLight,
      main: colors.SecondaryMain,
      dark: colors.SecondaryDark,
      contrastText: colors.White,
    },
    redesign: {
      pink: colors.PrimaryMain,
      white: colors.White,
      black: colors.Black,
      containerBg: colors.White,
      containerText: colors.Dark,
      navbarBg: colors.DarkToBlack,
      cardSurface: colors.CardSurfaceLight,
      membershipPlatinum: colors.redesignMembershipPlatinum,
      membershipGold: colors.redesignMembershipGold,
      membershipBronze: colors.redesignMembershipBronze,
      membershipClassPass: colors.redesignMembershipClassPass,
      primaryCta: colors.redesignPrimaryCta,
    },
  },
  dark: {
    colors: { ...commonThemeColors },
    background: {
      default: colors.Dark,
      paper: colors.Dark,
    },
    text: {
      primary: colors.White,
      muted: colors.Grey300, // subtitles and secondary text; bright enough to read clearly on dark
      secondary: colors.PrimaryMain,
    },
    primary: {
      light: colors.PrimaryLight,
      main: colors.PrimaryMain,
      dark: colors.PrimaryDark,
      contrastText: colors.White,
    },
    secondary: {
      light: colors.SecondaryLight,
      main: colors.SecondaryMain,
      dark: colors.SecondaryDark,
      contrastText: colors.White,
    },
    /* ── Mindscript redesign tokens ─────────────────────────── */
    redesign: {
      pink: colors.PrimaryMain,
      white: colors.Dark,
      black: colors.White,
      containerBg: colors.Grey800,
      containerText: colors.White,
      navbarBg: colors.DarkToBlack,
      cardSurface: colors.CardSurfaceDark,
      membershipPlatinum: colors.redesignMembershipPlatinum,
      membershipGold: colors.redesignMembershipGold,
      membershipBronze: colors.redesignMembershipBronze,
      membershipClassPass: colors.redesignMembershipClassPass,
      primaryCta: colors.redesignPrimaryCta,
    },
  },
};

export {
  fonts,
  themeColors,
  typographyScale,
  pageHeroTitleSize,
  pageHeroSubtitleSize,
  sectionTitleSize,
  sectionSubtitleSize,
  sectionSubtitleTwoLines,
  cardTitleSize,
  cardSubtitleSize,
  cardContentSize,
};
